import { useMutation, useQueryClient } from '@tanstack/react-query';

import type {
  Page,
  PatchAdminV1StoresStoreIdPagesIdBody,
  PostAdminV1StoresStoreIdPagesBody,
} from '@nucleus-commerce/api-client/admin';

import { redirectKeys } from '@/features/redirects';

import { pagesApi } from '../api/pages-api';

import { pageKeys } from './page-keys';

function remember(queryClient: ReturnType<typeof useQueryClient>, storeId: string, page: Page) {
  queryClient.setQueryData(pageKeys.detail(storeId, page.id), page);
  return Promise.all([
    queryClient.invalidateQueries({ queryKey: pageKeys.lists(storeId) }),
    // A rename writes a 301 on the backend, so the redirects list is stale too.
    queryClient.invalidateQueries({ queryKey: redirectKeys.all(storeId) }),
  ]);
}

/** 409 ALREADY_EXISTS on `handle`; the form shows it. */
export function useCreatePage(storeId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: PostAdminV1StoresStoreIdPagesBody) => pagesApi.create(storeId, body),
    meta: { successMessage: 'pages:toasts.created', silentError: true },
    onSuccess: (page) => remember(queryClient, storeId, page),
  });
}

export function useUpdatePage(storeId: string, successMessage = 'pages:toasts.updated') {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: PatchAdminV1StoresStoreIdPagesIdBody }) =>
      pagesApi.update(storeId, id, body),
    meta: { successMessage, silentError: true },
    onSuccess: (page) => remember(queryClient, storeId, page),
  });
}

export function useDeletePage(storeId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => pagesApi.remove(storeId, id),
    meta: { successMessage: 'pages:toasts.deleted', silentError: true },
    onSuccess: (_void, id) => {
      queryClient.removeQueries({ queryKey: pageKeys.detail(storeId, id) });
      return queryClient.invalidateQueries({ queryKey: pageKeys.lists(storeId) });
    },
  });
}
